<template>
    <main class="news-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/news" class="tbv2-crumb">{{ $t('news.page.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ pageTitle }}</span>
            </div>
        </portal>

        <div v-if="isLoading" class="nv-loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <template v-else-if="article">
            <!-- Cover hero -->
            <div v-if="coverUrl" class="nv-cover">
                <img :src="coverUrl" :alt="pageTitle" />
            </div>

            <!-- Header -->
            <header class="nv-header">
                <router-link to="/news" class="news-back">
                    <i class="fal fa-arrow-left"></i>
                    <span>{{ $t('common.back') }}</span>
                </router-link>

                <div class="nv-header-row">
                    <div class="nv-header-left">
                        <div class="nv-meta">
                            <span
                                class="nv-badge"
                                :class="article.status === 'published' ? 'nv-badge--pub' : 'nv-badge--draft'"
                            >
                                <i :class="article.status === 'published' ? 'fal fa-check-circle' : 'fal fa-pen'"></i>
                                {{ $t(`news.status.${article.status || 'draft'}`) }}
                            </span>
                            <span v-if="category" class="nv-category">
                                {{ category }}
                            </span>
                            <span v-if="article.published_at" class="nv-date">
                                {{ formatDate(article.published_at) }}
                            </span>
                            <span v-if="authorName" class="nv-author">
                                {{ $t('post.by') }} {{ authorName }}
                            </span>
                        </div>
                        <h1 class="nv-title">{{ pageTitle }}</h1>
                    </div>

                    <div v-if="canUpdate" class="nv-header-actions">
                        <BmvButton
                            v-if="article.status === 'draft'"
                            variant="success"
                            icon="fal fa-paper-plane"
                            :loading="busyAction === 'publish'"
                            @click="doPublish"
                        >
                            {{ $t('news.actions.publish') }}
                        </BmvButton>
                        <BmvButton
                            v-else
                            variant="secondary"
                            icon="fal fa-ban"
                            :loading="busyAction === 'unpublish'"
                            @click="doUnpublish"
                        >
                            {{ $t('news.actions.unpublish') }}
                        </BmvButton>
                        <BmvButton
                            variant="primary"
                            icon="fal fa-pen"
                            @click="goToEdit"
                        >
                            {{ $t('common.edit') }}
                        </BmvButton>
                    </div>
                </div>
            </header>

            <!-- Language tabs (only shown when EN content exists) -->
            <div v-if="hasEnContent" class="nv-tabs">
                <button
                    type="button"
                    class="nv-tab"
                    :class="{ 'is-active': activeLang === 'fr' }"
                    @click="activeLang = 'fr'"
                >
                    {{ $t('news.form.tab_fr') }}
                </button>
                <button
                    type="button"
                    class="nv-tab"
                    :class="{ 'is-active': activeLang === 'en' }"
                    @click="activeLang = 'en'"
                >
                    {{ $t('news.form.tab_en') }}
                </button>
            </div>

            <!-- Content -->
            <article class="nv-body">
                <div
                    v-if="currentContent"
                    class="nv-content ck-content"
                    v-html="currentContent"
                />
                <p v-else class="nv-empty">
                    {{ $t('news.page.view_no_content') }}
                </p>
            </article>
        </template>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'

export default {
    name: 'NewsView',
    components: { BmvButton },
    data() {
        return {
            isLoading: true,
            busyAction: null,
            activeLang: 'fr'
        }
    },
    computed: {
        ...mapState('news', { article: (state) => state.article }),
        lang() {
            return this.$store.state.lang
        },
        articleId() {
            return this.$route.params.id
        },
        translation() {
            if (!this.article) return null
            const translations = this.article.translations || []
            return (
                translations.find((t) => t.locale === this.activeLang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0] ||
                null
            )
        },
        pageTitle() {
            if (!this.article) return ''
            const translations = this.article.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && match.title) || `#${this.article.id}`
        },
        currentContent() {
            return this.translation && this.translation.content || ''
        },
        hasEnContent() {
            if (!this.article) return false
            const translations = this.article.translations || []
            const en = translations.find((t) => t.locale === 'en')
            return !!(en && (en.title || en.content))
        },
        coverUrl() {
            const media = this.article && this.article.cover_media
            if (!media) return null
            return media.base64 || media.url || media.path || null
        },
        category() {
            const cat = this.article && this.article.category
            if (!cat) return null
            const translations = cat.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && (match.name || match.title)) || cat.name || null
        },
        authorName() {
            const author = this.article && this.article.created_by
            if (!author) return null
            return author.name || author.email || null
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canUpdate() {
            return this.permissions.includes('news.update')
        }
    },
    created() {
        this.loadArticle()
    },
    methods: {
        ...mapActions('news', ['getArticle', 'publishArticle', 'unpublishArticle']),
        async loadArticle() {
            this.isLoading = true
            try {
                await this.getArticle(this.articleId)
                if (this.article) {
                    const translations = this.article.translations || []
                    const hasLang = translations.some((t) => t.locale === this.lang)
                    this.activeLang = hasLang ? this.lang : 'fr'
                }
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
                this.$router.push({ name: 'News' })
            } finally {
                this.isLoading = false
            }
        },
        formatDate(iso) {
            return this.moment(iso).locale(this.lang).format('Do MMMM YYYY')
        },
        goToEdit() {
            this.$router.push({ name: 'NewsEdit', params: { id: this.articleId } })
        },
        async doPublish() {
            if (this.busyAction) return
            this.busyAction = 'publish'
            try {
                await this.publishArticle(this.articleId)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('news.actions.publish') })
                await this.loadArticle()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyAction = null
            }
        },
        async doUnpublish() {
            if (this.busyAction) return
            this.busyAction = 'unpublish'
            try {
                await this.unpublishArticle(this.articleId)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('news.actions.unpublish') })
                await this.loadArticle()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyAction = null
            }
        }
    }
}
</script>

<style scoped>
.news-v2 {
    width: 100%;
    padding: 0 0 64px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.nv-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 80px 0;
}

/* Cover hero */
.nv-cover {
    width: 100%;
    max-height: 380px;
    overflow: hidden;
    background: var(--v2-surface-2);
}

.nv-cover img {
    width: 100%;
    height: 100%;
    max-height: 380px;
    object-fit: cover;
    display: block;
}

/* Header */
.nv-header {
    padding: 24px 32px 0;
    margin-bottom: var(--v2-space-6);
}

.news-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 16px;
    transition: color var(--v2-transition-fast);
}

.news-back:hover {
    color: var(--v2-primary);
}

.nv-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
}

.nv-header-left {
    flex: 1;
    min-width: 0;
}

.nv-header-actions {
    display: inline-flex;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
}

.nv-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
}

.nv-title {
    margin: 0;
    font-size: 1.9rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
    word-break: break-word;
}

/* Meta chips */
.nv-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.nv-badge i { font-size: 0.7rem; }

.nv-badge--draft {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.nv-badge--pub {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.nv-category {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
}

.nv-date,
.nv-author {
    font-size: 0.8rem;
    color: var(--v2-text-3);
}

/* Language tabs */
.nv-tabs {
    display: flex;
    gap: 4px;
    padding: 0 32px;
    border-bottom: 1px solid var(--v2-border);
    margin-bottom: var(--v2-space-6);
}

.nv-tab {
    padding: 8px 16px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.nv-tab:hover {
    color: var(--v2-text-1);
}

.nv-tab.is-active {
    color: var(--v2-primary);
    border-bottom-color: var(--v2-primary);
}

/* Body */
.nv-body {
    padding: 0 32px;
    max-width: 860px;
}

.nv-empty {
    color: var(--v2-text-4);
    font-style: italic;
    font-size: 0.9rem;
}

/* CKEditor content resets (same as NewsForm.vue) */
.nv-content.ck-content ::v-deep h2,
.nv-content.ck-content ::v-deep h3,
.nv-content.ck-content ::v-deep h4 {
    margin: 1.4em 0 0.5em;
    font-weight: 700;
    color: var(--v2-text-1);
    line-height: 1.25;
}

.nv-content.ck-content ::v-deep h2 { font-size: 1.45rem; }
.nv-content.ck-content ::v-deep h3 { font-size: 1.2rem; }
.nv-content.ck-content ::v-deep h4 { font-size: 1rem; }

.nv-content.ck-content ::v-deep p {
    margin: 0 0 1em;
    line-height: 1.7;
    color: var(--v2-text-2);
}

.nv-content.ck-content ::v-deep ul,
.nv-content.ck-content ::v-deep ol {
    padding-left: 1.6em;
    margin: 0 0 1em;
}

.nv-content.ck-content ::v-deep li {
    margin-bottom: 4px;
    line-height: 1.6;
}

.nv-content.ck-content ::v-deep blockquote {
    border-left: 3px solid var(--v2-primary);
    margin: 1.2em 0;
    padding: 8px 16px;
    color: var(--v2-text-3);
    font-style: italic;
    background: var(--v2-surface-1);
    border-radius: 0 var(--v2-radius-sm) var(--v2-radius-sm) 0;
}

.nv-content.ck-content ::v-deep table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 0.9rem;
}

.nv-content.ck-content ::v-deep th,
.nv-content.ck-content ::v-deep td {
    border: 1px solid var(--v2-border);
    padding: 8px 12px;
    text-align: left;
}

.nv-content.ck-content ::v-deep th {
    background: var(--v2-surface-2);
    font-weight: 600;
    color: var(--v2-text-1);
}

.nv-content.ck-content ::v-deep a {
    color: var(--v2-primary);
    text-decoration: underline;
}

.nv-content.ck-content ::v-deep img {
    max-width: 100%;
    border-radius: var(--v2-radius-sm);
}

@media (max-width: 900px) {
    .nv-header {
        padding: 16px 18px 0;
    }

    .nv-tabs {
        padding: 0 18px;
    }

    .nv-body {
        padding: 0 18px;
    }

    .nv-title {
        font-size: 1.5rem;
    }

    .nv-cover {
        max-height: 220px;
    }

    .nv-cover img {
        max-height: 220px;
    }
}
</style>
